import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const savedToken = localStorage.getItem("auth_token");
    if (!savedToken) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` },
        withCredentials: true,
      });
      setUser(response.data);
      setToken(savedToken);
    } catch {
      localStorage.removeItem("auth_token");
      setUser(null);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("auth_token", authToken);
  };

  const isAdmin = user?.role === "admin";
  const isSubAdmin = user?.role === "sub_admin";
  const isTeamLead = user?.role === "team_lead";
  const isEventHost = user?.role === "event_host";
  const isVendor = user?.role === "vendor";
  const canEdit = ["admin", "sub_admin", "team_lead"].includes(user?.role);
  const canDelete = ["admin", "sub_admin"].includes(user?.role);
  const canApprove = ["admin", "sub_admin"].includes(user?.role);
  const canManageUsers = user?.role === "admin";

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
    setToken(null);
    localStorage.removeItem("auth_token");
  };

  const authAxios = useCallback(() => {
    return axios.create({
      baseURL: API,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      withCredentials: true,
    });
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, checkAuth, authAxios, API, isAdmin, isSubAdmin, isTeamLead, isEventHost, isVendor, canEdit, canDelete, canApprove, canManageUsers }}>
      {children}
    </AuthContext.Provider>
  );
};
